import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { createUser, updateUser } from '../services/api/userApi';
import { getRoles } from '../services/api/roleApi';
import { validateInput, validateObject } from '../utils/sanitizer';

const UserForm = ({ user, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    sendWelcomeEmail: true,
    roleIds: []
  });
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await getRoles();
        setRoles(response.data);
      } catch (err) {
        console.error('Failed to fetch roles:', err);
      }
    };
    
    fetchRoles();
    
    if (user) {
      setFormData({
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        sendWelcomeEmail: false,
        roleIds: []
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Validate input but don't modify it
    if (!validateInput(value)) {
      setError('Input contains potentially unsafe content.');
      return;
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleRoleChange = (selectedOptions) => {
    const roleIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setFormData(prev => ({ ...prev, roleIds }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Validate at least one role is selected for new users
    if (!user && formData.roleIds.length === 0) {
      setError('At least one role must be selected.');
      setLoading(false);
      return;
    }

    // Validate input before submission
    const validation = validateObject(formData);
    if (!validation.isValid) {
      setError('Input contains potentially unsafe content.');
      setLoading(false);
      return;
    }

    try {
      if (user) {
        await updateUser(user.id, { ...formData, id: user.id });
        setSuccess('User updated successfully!');
      } else {
        await createUser(formData);
        setSuccess('User created successfully!');
      }
      setTimeout(() => {
        onSave();
      }, 1500);
    } catch (err) {
      let errorMessage = 'User save failed. Check your input or try again later.';
      
      if (err.response?.status === 400) {
        errorMessage = err.response?.data?.message || 'Invalid user information provided. Please check your input.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Error occurred while creating User. Please contact support if the problem persists.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-5">
          <div className="card">
            <div className="card-header">
              <h3 className="mb-0">{user ? 'Edit User' : 'Create User'}</h3>
            </div>
            <div className="card-body">
              <div className="user-form">
                {error && <div className="error">{error}</div>}
                {success && <div className="success" style={{color: 'green', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', padding: '10px', borderRadius: '4px', marginBottom: '15px'}}>{success}</div>}
                
                <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="firstName">First Name:</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="lastName">Last Name:</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            disabled={!!user}
          />
        </div>

        {!user && (
          <>
            <div className="form-group">
              <label htmlFor="roles">Roles:</label>
              <Select
                isMulti
                options={roles
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(role => ({
                    value: role.id,
                    label: role.name,
                    description: role.description
                  }))}
                value={roles
                  .filter(role => formData.roleIds.includes(role.id))
                  .map(role => ({
                    value: role.id,
                    label: role.name
                  }))}
                onChange={handleRoleChange}
                placeholder="Select roles..."
                formatOptionLabel={(option) => (
                  <div>
                    <div>{option.label}</div>
                    {option.description && <div style={{ fontSize: '0.85em', color: '#666' }}>{option.description}</div>}
                  </div>
                )}
              />
            </div>
            
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="sendWelcomeEmail"
                  checked={formData.sendWelcomeEmail}
                  onChange={(e) => setFormData({
                    ...formData,
                    sendWelcomeEmail: e.target.checked
                  })}
                />
                Send welcome email with temporary password
              </label>
            </div>
          </>
        )}

                  <div className="form-actions">
                    <button type="submit" disabled={loading} className="btn btn-primary">
                      {loading ? 'Saving...' : (user ? 'Update' : 'Create')}
                    </button>
                    <button type="button" onClick={onCancel} className="btn btn-secondary">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserForm;